/**
 * Collection-Based Discount Messaging Component
 * Displays discount messages on PDP and PLP based on product collection membership
 */

(function () {
    'use strict';

    let DISCOUNT_RULES = [];

    /**
     * Initialize discount rules from JSON script tag
     */
    function initDiscountRules() {
        const scriptEl = document.querySelector('[data-discount-json]');
        if (!scriptEl) {
            console.warn('[Discount] No discount rules found on page');
            return;
        }

        const raw = scriptEl.textContent || scriptEl.innerText || '';
        if (!raw.trim()) {
            console.warn('[Discount] Discount rules script is empty');
            return;
        }

        try {
            DISCOUNT_RULES = JSON.parse(raw);
        } catch (error) {
            console.error('Discount rules JSON parse error', error);
            DISCOUNT_RULES = [];
        }
    }

    /**
     * Find the best matching discount rule for given collection handles
     * @param {string} collectionString - Pipe-separated collection handles
     * @returns {Object|null} - Matching discount rule or null
     */
    function getDiscountForCollections(collectionString) {
        if (!collectionString || !DISCOUNT_RULES.length) {
            return null;
        }

        const productHandles = collectionString
            .split('|')
            .map(h => h.trim())
            .filter(Boolean);

        if (!productHandles.length) {
            return null;
        }

        const rules = [...DISCOUNT_RULES].sort((a, b) => (b.priority || 0) - (a.priority || 0));

        for (const rule of rules) {
            const ruleCollections = Array.isArray(rule.collections)
                ? rule.collections
                : [];

            if (!ruleCollections.length) continue;

            const hasMatch = productHandles.some(handle =>
                ruleCollections.includes(handle)
            );

            if (hasMatch) {
                return rule;
            }
        }

        return null;
    }

    /**
     * Apply discount message to a target element
     * @param {string} collectionString - Pipe-separated collection handles
     * @param {HTMLElement} containerEl - Target element for message
     */
    function applyDiscountMessaging(collectionString, containerEl) {
        const rule = getDiscountForCollections(collectionString);
        containerEl.className = containerEl.className.replace(/\bblock-id-\S+\b/g, '').trim();

        if (!rule) {
            containerEl.innerHTML = '';
            containerEl.style.removeProperty('color');
            containerEl.parentElement?.classList.remove('has-discount-message');
            return;
        }

        containerEl.innerHTML = rule.productMessage || '';

        if (rule.textColor) {
            containerEl.style.color = rule.textColor;
        } else {
            containerEl.style.removeProperty('color');
        }

        if (rule.block_id) {
            containerEl.classList.add(`block-id-${rule.block_id}`);
        }

        containerEl.parentElement?.classList.add('has-discount-message');
    }

    /**
     * Initialize discount messaging for all elements on page
     */
    function initDiscountMessagingForPage() {
        const wrappers = document.querySelectorAll('[data-discount-collections]');

        wrappers.forEach((wrapper, index) => {
            const collections = wrapper.getAttribute('data-discount-collections');
            const target = wrapper.querySelector('[data-discount-target]');

            if (!target) {
                return;
            }
            applyDiscountMessaging(collections, target);
        });
    }

    /**
     * Custom element for discount messaging
     */
    if (!customElements.get('collection-discount')) {
        customElements.define('collection-discount', class extends HTMLElement {
            connectedCallback() {
                const collections = this.getAttribute('data-discount-collections');
                const target = this.querySelector('[data-discount-target]');

                if (!target) return;

                applyDiscountMessaging(collections, target);
            }
        });
    }

    /**
     * Initialize on DOM ready
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initDiscountRules();
            initDiscountMessagingForPage();
        });
    } else {
        initDiscountRules();
        initDiscountMessagingForPage();
    }

    // Re-initialize when new content is loaded (e.g., AJAX pagination)
    document.addEventListener('theme:product:loaded', () => {
        initDiscountMessagingForPage();
    });

    // Re-initialize when recently viewed products are loaded
    document.addEventListener('theme:recently-viewed:loaded', () => {
        initDiscountMessagingForPage();
    });

    /**
     * Watch for dynamically loaded products (related products, AJAX content, etc.)
     */
    const observeProductContainers = () => {
        const containers = document.querySelectorAll('[data-related-products], recently-viewed, [data-products-container], [data-collection-products]');

        containers.forEach(container => {
            const observer = new MutationObserver((mutations) => {
                const hasNewProducts = mutations.some(mutation =>
                    Array.from(mutation.addedNodes).some(node =>
                        node.nodeType === 1 && (
                            node.hasAttribute?.('data-product-block') ||
                            node.querySelector?.('[data-product-block]')
                        )
                    )
                );

                if (hasNewProducts) {
                    initDiscountMessagingForPage();
                }
            });

            observer.observe(container, {
                childList: true,
                subtree: true
            });
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', observeProductContainers);
    } else {
        observeProductContainers();
    }

})();
